/**
 * Vehicles hook — TanStack Query for nearby vehicles + favorites.
 */
import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vehicleService } from '../services/vehicle.service'
import { useAuth } from './use-auth'
import { useLocation } from './use-location'
import { useExploreStore } from '../stores/explore.store'
import type { VehicleSearchFilters } from '../types/vehicle.types'
import { sortMarketplaceShopperRank } from '../utils/marketplace-shopper-rank'

export const vehicleKeys = {
  all: ['vehicles'] as const,
  nearby: (lat: number, lng: number) => [...vehicleKeys.all, 'nearby', lat, lng] as const,
  search: (filters: VehicleSearchFilters) => [...vehicleKeys.all, 'search', filters] as const,
  favorites: (userId: string) => [...vehicleKeys.all, 'favorites', userId] as const,
}

const EMPTY_FAVORITES = new Set<string>()

type VehicleListScope = 'nearby' | 'all'

interface UseVehiclesOptions {
  /**
   * nearby: marketplace surfaces scoped around the user's/default location.
   * all: full visible catalog, used by "Ver todos" so inventory is not
   * accidentally hidden by the nearby radius.
   */
  scope?: VehicleListScope
}

function stableCoord(value: number): number {
  return Number(value.toFixed(3))
}

export function useVehicles(options: UseVehiclesOptions = {}) {
  const queryClient = useQueryClient()
  // The store owns the scope; an explicit option still wins, because
  // all-vehicles.tsx is a screen whose entire job is "everything".
  const storeScope = useExploreStore((s) => s.locationScope)
  const requestedScope = options.scope ?? storeScope
  const { user } = useAuth()
  const { location } = useLocation()
  const category = useExploreStore((s) => s.category)
  const transmission = useExploreStore((s) => s.transmission)
  const minPriceCents = useExploreStore((s) => s.minPriceCents)
  const maxPriceCents = useExploreStore((s) => s.maxPriceCents)
  const minYear = useExploreStore((s) => s.minYear)
  const isInstantBook = useExploreStore((s) => s.isInstantBook)
  const requiredFeatures = useExploreStore((s) => s.requiredFeatures)
  const pickupDate = useExploreStore((s) => s.pickupDate)
  const returnDate = useExploreStore((s) => s.returnDate)
  const query = useExploreStore((s) => s.query)

  const activeFilterCount = [
    transmission,
    minPriceCents != null ? minPriceCents : null,
    maxPriceCents != null ? maxPriceCents : null,
    minYear,
    isInstantBook,
    ...requiredFeatures,
  ].filter((v) => v != null).length

  // "Near me" without a coordinate is not a search around a guess — it is not a
  // near-me search at all. This used to fall back to a hardcoded San Salvador,
  // which is why granting the permission from abroad returned nothing while
  // denying it returned everything. Missing location now degrades to the whole
  // country, which is the honest superset, never to somebody else's city.
  const scope = requestedScope === 'nearby' && location ? 'nearby' : 'all'
  const searchLat = location ? stableCoord(location.lat) : undefined
  const searchLng = location ? stableCoord(location.lng) : undefined

  const searchFilters = useMemo<VehicleSearchFilters>(
    () => ({
      query: query.trim() || undefined,
      transmission: transmission ?? undefined,
      startDate: pickupDate ?? undefined,
      endDate: returnDate ?? undefined,
      lat: scope === 'nearby' ? searchLat : undefined,
      lng: scope === 'nearby' ? searchLng : undefined,
      radiusKm: scope === 'nearby' ? 50 : undefined,
      minPrice: minPriceCents ?? undefined,
      maxPrice: maxPriceCents ?? undefined,
      minYear: minYear ?? undefined,
      requiredFeatures: requiredFeatures.length > 0 ? requiredFeatures : undefined,
      limit: 100,
    }),
    [
      scope,
      query,
      transmission,
      pickupDate,
      returnDate,
      searchLat,
      searchLng,
      minPriceCents,
      maxPriceCents,
      minYear,
      requiredFeatures,
    ],
  )

  const vehiclesQuery = useQuery({
    queryKey: vehicleKeys.search(searchFilters),
    queryFn: async () => {
      return vehicleService.searchVehicles(searchFilters)
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Keep previous data visible while location or filters settle so the
    // marketplace never flashes empty between valid result sets.
    placeholderData: (prev) => prev,
  })

  // Near you found NOTHING: fall back to the whole country, and say so.
  //
  // The scope logic above already covers the person with no location — they get
  // the country. The person it abandoned was the one WITH a location that is
  // far from the inventory: a 50 km search around Miami or Mountain View
  // returns zero, and the marketplace rendered an empty shell to exactly the
  // person planning a trip to El Salvador from abroad — a core persona for a
  // single-country rental app, and the state CI's emulators (which boot at
  // Google HQ) had been failing on for two days while local emulators with no
  // location fix sailed through.
  //
  // The fallback is a second query, not a widening of the first: the nearby
  // result staying empty is itself information ("no hay cerca de vos"), and the
  // map screen must keep strictly-nearby semantics, so the RPC is not touched.
  // `nearbyCameBackEmpty` is exposed for the UI to say honestly what happened
  // rather than silently teleporting the person's search.
  const nearbyCameBackEmpty =
    scope === 'nearby' && vehiclesQuery.isSuccess && (vehiclesQuery.data?.length ?? 0) === 0

  const countryFallbackQuery = useQuery({
    queryKey: vehicleKeys.search({
      ...searchFilters,
      lat: undefined,
      lng: undefined,
      radiusKm: undefined,
    }),
    queryFn: async () => {
      return vehicleService.searchVehicles({
        ...searchFilters,
        lat: undefined,
        lng: undefined,
        radiusKm: undefined,
      })
    },
    enabled: nearbyCameBackEmpty,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  const favoritesQuery = useQuery({
    queryKey: vehicleKeys.favorites(user?.id ?? ''),
    queryFn: () => vehicleService.getFavoriteIds(user!.id),
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  })

  const toggleFavoriteMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.toggleFavorite(user!.id, vehicleId),
    onMutate: async (vehicleId) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: vehicleKeys.favorites(user!.id) })
      const prev = queryClient.getQueryData<Set<string>>(vehicleKeys.favorites(user!.id))
      queryClient.setQueryData(vehicleKeys.favorites(user!.id), () => {
        const next = new Set(prev)
        if (next.has(vehicleId)) next.delete(vehicleId)
        else next.add(vehicleId)
        return next
      })
      return { prev }
    },
    onError: (_err, _vehicleId, context) => {
      if (context?.prev) {
        queryClient.setQueryData(vehicleKeys.favorites(user!.id), context.prev)
      }
    },
  })

  const usingCountryFallback = nearbyCameBackEmpty && (countryFallbackQuery.data?.length ?? 0) > 0

  const allVehicles = useMemo(
    () => (usingCountryFallback ? (countryFallbackQuery.data ?? []) : (vehiclesQuery.data ?? [])),
    [usingCountryFallback, countryFallbackQuery.data, vehiclesQuery.data],
  )

  // Filter by category + instant book if selected
  const filteredVehicles = useMemo(() => {
    let result = allVehicles
    if (category) {
      result = result.filter((v) => 'category' in v && v.category === category)
    }
    if (isInstantBook) {
      result = result.filter((v) => 'isInstantBook' in v && v.isInstantBook)
    }
    // Nearby with a real origin stays distance-first from the RPC. The
    // unfiltered country catalog — default Explore, "Ver todos", and the
    // nearby→country fallback — is a shop window: premium, then pickups,
    // then the rest. Recency is not a merchandising signal.
    const merchandisingBrowse = scope !== 'nearby' || usingCountryFallback
    if (merchandisingBrowse) {
      result = sortMarketplaceShopperRank(result)
    }
    return result
  }, [allVehicles, category, isInstantBook, scope, usingCountryFallback])

  // Popular = sorted by rating count (most reviewed). Derived from the
  // *filtered* set: sorting the raw response meant "Populares" kept showing
  // cars the active category/instant-book filters had already excluded from
  // the list right above it.
  const popularVehicles = useMemo(
    () => [...filteredVehicles].sort((a, b) => b.ratingCount - a.ratingCount).slice(0, 6),
    [filteredVehicles],
  )

  const favoriteIds = favoritesQuery.data ?? EMPTY_FAVORITES

  return {
    vehicles: filteredVehicles,
    popularVehicles,
    favoriteIds,
    toggleFavorite: toggleFavoriteMutation.mutate,
    // True while the country-wide fallback is standing in for an empty nearby
    // result. The UI uses it to say "no hay cerca de vos — esto es todo El
    // Salvador" instead of silently teleporting the search.
    usingCountryFallback,
    // The fallback's first fetch is part of "still loading" as far as the
    // screen is concerned: nearby resolving empty is not a settled answer until
    // the country query has had its chance to fill in.
    isLoading: vehiclesQuery.isLoading || (nearbyCameBackEmpty && countryFallbackQuery.isLoading),
    // `isLoading` is only true on the very first fetch. Because the query keeps
    // the previous result via `placeholderData`, a manual re-search resolves
    // with the list still on screen and nothing ever appears to happen —
    // surfaces that own a search button must render `isFetching` instead.
    isFetching: vehiclesQuery.isFetching,
    isRefetching: vehiclesQuery.isRefetching,
    error: vehiclesQuery.error,
    refetch: vehiclesQuery.refetch,
    activeFilterCount,
  }
}
